import { useState, useEffect } from "react";
import { apiErrorText } from '../lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import ServiceToggle from "./ServiceToggle";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { 
  Link2, 
  Unlink, 
  ExternalLink, 
  Loader2, 
  Plus, 
  AlertTriangle,
  Check,
  X,
  DollarSign,
  Clock,
  Building2
} from "lucide-react";
import { toast } from "sonner";
import api from "../utils/api";

const SharedServicesTab = () => {
  const [sharedServices, setSharedServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [shareToken, setShareToken] = useState("");
  const [linking, setLinking] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [unlinkingId, setUnlinkingId] = useState(null);

  useEffect(() => {
    loadSharedServices();
  }, []);

  const loadSharedServices = async () => {
    try {
      setLoading(true);
      const response = await api.get('/shared-services');
      setSharedServices(response.data.sharedServices || []);
    } catch (error) {
      console.error("Error loading shared services:", error);
      toast.error("Failed to load shared services");
    } finally {
      setLoading(false);
    }
  };

  const handleLinkService = async () => {
    if (!shareToken.trim()) {
      toast.error("Please enter a share token");
      return;
    }

    try {
      setLinking(true);
      const response = await api.post('/shared-services/link', {
        shareToken: shareToken.trim()
      });
      toast.success(response.data.message);
      setShowLinkModal(false);
      setShareToken("");
      loadSharedServices();
    } catch (error) {
      console.error("Error linking service:", error);
      toast.error(apiErrorText(error, "Failed to link service"));
    } finally {
      setLinking(false);
    }
  };

  const handleToggleLive = async (link) => {
    try {
      setTogglingId(link.id);
      const response = await api.put(`/shared-services/${link.id}/toggle`);
      toast.success(response.data.message);
      loadSharedServices();
    } catch (error) {
      console.error("Error toggling service:", error);
      toast.error(apiErrorText(error, "Failed to toggle service"));
    } finally {
      setTogglingId(null);
    }
  };

  const handleUnlink = async (link) => {
    if (!confirm(`Are you sure you want to unlink "${link.sourceServiceName}"? This will remove it from your widget.`)) {
      return;
    }

    try {
      setUnlinkingId(link.id);
      const response = await api.delete(`/shared-services/${link.id}`);
      toast.success(response.data.message);
      loadSharedServices();
    } catch (error) {
      console.error("Error unlinking service:", error);
      toast.error(apiErrorText(error, "Failed to unlink service"));
    } finally {
      setUnlinkingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'token_expired':
        return <Badge className="bg-red-100 text-red-700">Token Expired</Badge>;
      case 'unavailable':
        return <Badge className="bg-gray-100 text-gray-700">Unavailable</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-700">Unknown</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Shared Services</h2>
          <p className="text-sm text-gray-500">
            Services from other companies that you can sell on your widget
          </p>
        </div>
        <Button onClick={() => setShowLinkModal(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Link Service
        </Button>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="py-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : sharedServices.length === 0 ? (
        /* Empty State */
        <Card>
          <CardContent className="py-12 text-center">
            <Link2 className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Shared Services Yet</h3>
            <p className="text-gray-500 mb-4 max-w-md mx-auto">
              Link services from partner companies to sell them on your widget and earn commission.
            </p>
            <Button onClick={() => setShowLinkModal(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              Link Your First Service
            </Button>
          </CardContent>
        </Card>
      ) : (
        /* Service Cards */
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {sharedServices.map((link) => (
            <Card key={link.id} className={`relative ${link.status === 'token_expired' ? 'border-red-200 bg-red-50' : ''}`}>
              {/* Status Badge */}
              <div className="absolute top-3 right-3">
                {getStatusBadge(link.status)}
              </div>

              <CardContent className="p-4">
                {/* Service Info */}
                <div className="mb-4">
                  <h3 className="font-semibold text-base mb-1 pr-20">
                    {link.currentServiceData?.name || link.sourceServiceName}
                  </h3>
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    <Building2 className="w-3.5 h-3.5" />
                    <span>{link.sourceOrgName}</span>
                  </div>
                </div>

                {/* Service Details (if available) */}
                {link.currentServiceData && link.status === 'active' && (
                  <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-4">
                    <span className="flex items-center gap-1">
                      <DollarSign className="w-3.5 h-3.5" />
                      {link.currentServiceData.currency === 'AED' ? 'AED ' : '$'}
                      {link.currentServiceData.price}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      {link.currentServiceData.duration}min
                    </span>
                  </div>
                )}

                {/* Commission */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-green-700">Your Commission</span>
                    <span className="font-semibold text-green-700">
                      {link.currentCommission || link.commission}%
                    </span>
                  </div>
                </div>

                {/* Error Message */}
                {link.status === 'token_expired' && (
                  <div className="bg-red-100 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                    <div className="text-sm text-red-700">
                      {link.statusMessage || "The service owner rotated their share token. Please get a new token to reconnect."}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-2 border-t">
                  {/* Live Toggle */}
                  {link.status === 'active' && (
                    <div className="flex items-center gap-2">
                      <ServiceToggle
                        checked={link.isLive}
                        onChange={() => handleToggleLive(link)}
                        disabled={togglingId === link.id}
                      />
                      <span className={`text-sm ${link.isLive ? 'text-blue-600' : 'text-gray-500'}`}>
                        {togglingId === link.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : link.isLive ? 'Live' : 'Hidden'}
                      </span>
                    </div>
                  )}

                  {/* Unlink Button */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleUnlink(link)}
                    disabled={unlinkingId === link.id}
                    className="text-red-600 hover:text-red-700"
                  >
                    {unlinkingId === link.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Unlink className="w-3.5 h-3.5 mr-1" />
                        Unlink
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Link Service Modal */}
      <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-blue-600" />
              Link a Shared Service
            </DialogTitle>
            <DialogDescription>
              Enter the share token provided by the service owner to add their service to your widget.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="shareToken">Share Token</Label>
              <Input
                id="shareToken"
                placeholder="share_Xk9mP2nQ..."
                value={shareToken}
                onChange={(e) => setShareToken(e.target.value)}
                className="font-mono"
              />
              <p className="text-xs text-gray-500">
                Ask your partner for their share token. It starts with &ldquo;share_&rdquo;
              </p>
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex gap-2 text-sm text-blue-800">
                <ExternalLink className="w-4 h-4 shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">What happens next?</p>
                  <ul className="mt-1 space-y-0.5 text-blue-700">
                    <li>• The service will appear in your widget</li>
                    <li>• You&apos;ll earn commission on each booking</li>
                    <li>• Bookings are handled by the service owner</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleLinkService} disabled={linking || !shareToken.trim()}>
              {linking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Linking...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Link Service
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SharedServicesTab;
